<script setup>

/*==================================================================*/
/*================== VUE IMPORTS ===================================*/
/*==================================================================*/

import { defineProps, defineEmits, useSlots, computed } from 'vue';

/*==================================================================*/
/*================== IMPORTS DE COMPONENTES ========================*/
/*==================================================================*/

import TelaTabelaBase from '@/components/v2/base/TelaTabelaBase/TelaTabelaBase.vue';
import CampoAutocompleteBase from '@/components/v2/base/CampoAutocompleteBase/CampoAutocompleteBase.vue';
import ChipUsuario from '@/components/v2/compostos/ChipUsuario/ChipUsuario.vue';

/*==================================================================*/
/*================== PROPS =========================================*/
/*==================================================================*/

const props = defineProps({
    disabled: {
        type: Boolean,
        default: false
    },
    filtros: {
        type: [Array, Object],
        required: true
    },
    headers: {
        type: Array,
        default: () => []
    },
    dados: {
        type: Array,
        default: () => []
    },
    selecao: {
        type: String,
        default: 'nenhum'
    },
    selecionados: {
        type: Array,
        default: () => []
    },
    selecionado: {
        type: Object,
        default: () => ({})
    },
    itemKey: {
        type: String,
        default: 'id'
    },
    paginacao: {
        type: String,
        default: 'nenhuma'
    },
    itensPorPagina: {
        type: Number,
        default: 10
    }
});

const emits = defineEmits([
    'update:selecionados',
    'update:selecionado',
]);

const slots = useSlots();

/*==================================================================*/
/*================== ALTERA SELECIONADOS/SELECIONADO ================*/
/*==================================================================*/

const alteraSelecionados = (selecionados) => {

    emits('update:selecionados', selecionados);

};

const alteraSelecionado = (selecionado) => {

    emits('update:selecionado', selecionado);

};

/*==================================================================*/
/*================== SLOTS DE ITEM =================================*/
/*==================================================================*/

const slotsDeItem = computed(() =>

    Object.keys(slots).filter(name => name.startsWith('tabela-linha-item.'))

);

</script>

<template>

    <TelaTabelaBase
        :disabled="props.disabled"
        :headers="props.headers"
        :dados="props.dados"
        :selecao="props.selecao"
        :selecionados="props.selecionados"
        :selecionado="props.selecionado"
        :item-key="props.itemKey"
        :paginacao="props.paginacao"
        :itens-por-pagina="props.itensPorPagina"
        @update:selecionados="alteraSelecionados"
        @update:selecionado="alteraSelecionado"
    >

        <template #tela-titulo>
            <slot name="tela-titulo"></slot>
        </template>

        <template v-if="slots['tabela-linha-botoes']" #tabela-linha-botoes="slotProps">
            <slot name="tabela-linha-botoes" v-bind="slotProps"></slot>
        </template>

        <template
            v-for="name in slotsDeItem"
            #[name]="slotProps"
        >

            <slot :name="name" v-bind="slotProps ?? {}"></slot>

        </template>

        <template #card-header>

            <div class="c-card-header">

                <div class="c-card-header__coluna-esquerda">

                     <div class="c-card-header__filtros">

                        <div class="c-filtros-campos">

                            <CampoAutocompleteBase
                                v-for="(filtro, index) in props.filtros"
                                :key="index"
                                :label="filtro.label"
                                :items="filtro.items"
                                :item-text="filtro.itemText"
                                :disabled="props.disabled"
                                return-object
                                @change="filtro.onChange"
                            ></CampoAutocompleteBase>

                        </div>

                    </div>

                    <div
                        v-if="$slots['toolbar-baixo-esquerda']"
                        class="c-card-header__acoes-esquerda"
                    >
                        <slot name="toolbar-baixo-esquerda"></slot>
                    </div>

                </div>

                <div class="c-card-header__coluna-direita">

                    <div class="c-card-header__usuario">

                        <ChipUsuario
                            class="c-chip-usuario"
                            backgroundColor="#e0e0e0"
                            textColor="#000000"
                            size="48px"
                        ></ChipUsuario>

                    </div>


                    <div
                        v-if="$slots['toolbar-baixo-direita']"
                        class="c-card-header__acoes-direita"
                    >
                        <slot name="toolbar-baixo-direita"></slot>
                    </div>

                </div>

            </div>

        </template>

        <template #card-footer>
            <slot name="card-footer"></slot>
        </template>

        <template #modais>
            <slot name="modais"></slot>
        </template>

    </TelaTabelaBase>

</template>

<style scoped>

.c-card-header{
    display: grid;
    grid-template-columns: 1fr 1fr;
    grid-auto-rows: max-content;
    grid-template-areas:
        "filtros direita"
        "baixoEsquerda direita";
    gap: 10px;
}

.c-card-header__coluna-esquerda{
    display: contents;
}

.c-card-header__coluna-direita{
    display: grid;
    grid-template-columns: 1fr;
    grid-auto-rows: max-content;
    gap: 10px;
    grid-area: direita;
}

.c-card-header__filtros {
    grid-area: filtros;
}

.c-filtros-campos {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 10px;
}

.c-filtros-campos > * {
    flex: 1 1 200px;
}

.c-card-header__acoes-esquerda {
    grid-area: baixoEsquerda;
}

.c-chip-usuario{
    display: grid;
    justify-content: center;
}

@media (max-width: 768px) {

    .c-card-header{
        display: grid;
        grid-template-columns: 1fr;
        grid-auto-rows: max-content;
        grid-template-areas:
            "usuario"
            "filtros "
            "baixoDireita"
            "baixoEsquerda";
        gap: 10px;
    }

    .c-card-header__coluna-esquerda{
        display: contents;
    }

    .c-card-header__coluna-direita{
        display: contents;
    }

    .c-card-header__usuario {
        grid-area: usuario;
    }

    .c-card-header__filtros {
        grid-area: filtros;
    }

    .c-card-header__acoes-esquerda {
        grid-area: baixoEsquerda;
    }

    .c-card-header__acoes-direita {
        grid-area: baixoDireita;
    }

}

</style>
